/**
 * Generic accordion / disclosure block used across long-form pages
 * (How-to, Docs, Use cases). Pure CSS for the chevron rotation; no
 * external dep. Each item is independently controlled by a Map of ids
 * managed by the parent so we can also offer Expand/Collapse all and
 * auto-open on search hit.
 *
 * UX choices that matter:
 *  - Only the first section is open by default (the rest must be earned).
 *  - When the parent passes `forceOpen`, the disclosure ignores its local
 *    state — used during search so matching sections always reveal.
 *  - The summary row is keyboard-friendly (it's a real <button>) and the
 *    body is announced via aria-controls/aria-expanded.
 */
import { ChevronRight, type LucideIcon } from "lucide-react";
import { useState, type ReactNode } from "react";

interface Props {
  id?: string;
  title: ReactNode;
  Icon?: LucideIcon;
  /** Right-side metadata (pills, persona, count). Hidden on small screens. */
  meta?: ReactNode;
  /** Right-side action button (e.g. "Try it"). */
  action?: ReactNode;
  defaultOpen?: boolean;
  /** Externally forced state. When boolean, overrides local toggle. */
  forceOpen?: boolean;
  children: ReactNode;
}

export function Disclosure({
  id, title, Icon, meta, action, defaultOpen = false, forceOpen, children,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = forceOpen ?? open;
  const panelId = `${id || "disc"}-body`;

  return (
    <section id={id} className="card scroll-mt-4 p-0">
      <header className="flex items-center justify-between gap-3 px-4 py-3">
        <button
          type="button"
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
          className="flex flex-1 items-center gap-3 text-left"
        >
          <ChevronRight
            size={16}
            className={`shrink-0 text-muted transition-transform ${isOpen ? "rotate-90 text-accent" : ""}`}
          />
          {Icon && <Icon size={18} className="shrink-0 text-accent" />}
          <h2 className="flex-1 text-base font-semibold leading-tight">{title}</h2>
          {meta && <div className="hidden text-xs text-muted md:block">{meta}</div>}
        </button>
        {action && <div className="shrink-0">{action}</div>}
      </header>
      {isOpen && (
        <div id={panelId} className="border-t border-border/60 px-4 py-4">
          {children}
        </div>
      )}
    </section>
  );
}
